import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { XCircle, CreditCard } from 'lucide-react';
import ManagementPage from '../../Common/components/common/ManagementPage';
import DetailItem from '../../Common/components/common/DetailItem';
import DetailGrid from '../../Common/components/common/DetailGrid';
import { useToast } from '../../Common/context/ToastContext';
import axios from 'axios';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';

dayjs.extend(utc);

// UTC → 한국시간(KST, UTC+9) 포맷
function formatKST(dateStr, format = 'YYYY.MM.DD HH:mm') {
  if (!dateStr) return '-';
  return dayjs.utc(dateStr).add(9, 'hour').format(format);
}

// AppPaymentList.js와 동일한 카드명 파싱
function parseCardNameFromRaw(pgRawResponse) {
  if (!pgRawResponse || typeof pgRawResponse !== 'string') return '-';
  try {
    const parsed = JSON.parse(pgRawResponse);
    const data = parsed?.data;
    return data?.cardNm || data?.cardKind || '-';
  } catch (_) {
    return '-';
  }
}

// API 응답 → 화면에서 쓰기 좋은 트랜잭션 형태로 변환
function mapPaymentToTransaction(item) {
  const paymentTransactionId =
    item.payment_transaction_id ??
    item.paymentTransactionId ??
    item.id ??
    item.paymentId ??
    item.payment_id;
  const id = paymentTransactionId;

  const dateStr =
    item.approved_at ??
    item.approvedAt ??
    item.approval_date ??
    item.approvalDate ??
    item.created_at ??
    item.createdAt ??
    item.date;
  const date = dateStr ? dayjs.utc(dateStr).toISOString() : dayjs().toISOString();

  const instmtMon =
    item.instmt_mon ??
    item.instmtMon ??
    item.pg_instmt_mon ??
    item.installmentMonths ??
    item.installment_months ??
    '00';
  const type = instmtMon === '00' ? '일시불' : `${instmtMon}개월`;

  const cardName =
    item.card_nm ??
    item.cardNm ??
    item.cardName ??
    parseCardNameFromRaw(item.pg_raw_response) ??
    '-';

  const status = item.status || '';

  return {
    id,
    paymentTransactionId,
    paymentId: paymentTransactionId,
    date,
    pharmacy:
      item.mcht_cust_nm ??
      item.mchtCustNm ??
      item.pharmacy ??
      item.customer_name ??
      item.pharmacy_name ??
      item.customerName ??
      item.pharmacyName ??
      '-',
    amount: Number(
      item.amount ??
        item.trdAmt ??
        item.trd_amt ??
        item.pg_trd_amt ??
        item.request_trd_amt ??
        0
    ),
    approvalNo:
      item.appr_no ??
      item.apprNo ??
      item.approvalNo ??
      item.approval_no ??
      item.approvalNumber ??
      '',
    fee: Number(
      item.fee_amt ??
        item.feeAmt ??
        item.fee_amount ??
        item.fee ??
        0
    ),
    // API의 salesperson_name 필드를 우선 사용
    driver:
      item.salesperson_name ??
      item.salespersonName ??
      item.salesperson ??
      item.driver ??
      item.driver_name ??
      item.driverName ??
      '',
    type,
    cancelStatus: String(status).toUpperCase() === 'CANCELED' || item.canceled_at != null,
    status,
    cardName,
  };
}

function getStatusLabelFromTx(tx) {
  if (tx.cancelStatus) return '취소';
  return '승인';
}

function PaymentList() {
  const { showToast } = useToast();
  const navigate = useNavigate();
  const location = useLocation();
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedItem, setSelectedItem] = useState(null);
  // 기본 기간: 당월 1일 ~ 오늘 (정산달력에서 넘어온 경우 해당 날짜 사용)
  const getDefaultMonthRange = () => {
    const today = new Date();
    const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
    const formatDate = (d) => `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
    return {
      start: formatDate(monthStart),
      end: formatDate(today),
    };
  };
  const defaultRange = getDefaultMonthRange();
  const initialDate = location.state?.searchDate ?? defaultRange.end;
  const [startDate, setStartDate] = useState(location.state?.searchDate ? initialDate : defaultRange.start);
  const [endDate, setEndDate] = useState(initialDate);
  const [searchInput, setSearchInput] = useState(''); // 검색창 입력값
  const [searchQuery, setSearchQuery] = useState(''); // API q 파라미터 (검색 버튼/Enter 시 적용)

  // 정산달력에서 "전체내역"으로 올 때 전달된 날짜 적용 후 state 제거(URL 유지)
  useEffect(() => {
    if (location.state?.searchDate) {
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, []);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [receiptEmail, setReceiptEmail] = useState('');
  const [isSendingReceipt, setIsSendingReceipt] = useState(false);

  // 관리자 결제내역과 동일한 CSV 컬럼 구성
  const buildCsv = (data) => {
    const columns = [
      { key: 'date', label: '결제일' },
      { key: 'pharmacy', label: '약국명' },
      { key: 'status', label: '상태' },
      { key: 'amount', label: '결제금액' },
      { key: 'fee', label: '수수료' },
      { key: 'driver', label: '영업자' },
      { key: 'type', label: '할부' },
      { key: 'approvalNo', label: '승인번호' },
      { key: 'paymentTransactionId', label: '거래ID' },
    ];

    const escapeCell = (value) => {
      if (value === null || value === undefined) return '';
      return String(value).replace(/"/g, '""');
    };

    const header = columns.map((c) => `"${c.label}"`).join(',');
    const lines = data.map((row) =>
      columns
        .map((c) => {
          let v = row[c.key];
          if (c.key === 'date') {
            v = formatKST(row.date, 'YYYY.MM.DD HH:mm');
          } else if (c.key === 'amount') {
            v = Number(row.amount || 0);
          } else if (c.key === 'fee') {
            v = Number(row.fee || 0);
          } else if (c.key === 'status') {
            v = getStatusLabelFromTx(row);
          }
          return `"${escapeCell(v ?? '')}"`;
        })
        .join(','),
    );

    // UTF-8 BOM 추가 (윈도우 엑셀 한글 깨짐 방지)
    return '\uFEFF' + header + '\r\n' + lines.join('\r\n');
  };

  const setQuickPeriod = (type) => {
    const today = new Date();
    const formatDate = (d) =>
      `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(
        d.getDate()
      ).padStart(2, '0')}`;
    const parseDate = (s) => {
      const [y, m, d] = s.split('.').map(Number);
      return new Date(y, m - 1, d);
    };
    let currentStart = parseDate(startDate);
    let currentEnd = parseDate(endDate);
    let newStart, newEnd;
    switch (type) {
      case '당일':
        newStart = newEnd = today;
        break;
      case '전일':
        // 현재 조회 구간의 "마지막날(endDate)" 기준으로 전일로 이동
        // 예) 2/1~2/9 상태에서 '전일' 클릭 시 2/8 하루로 이동
        newEnd = new Date(currentEnd);
        newEnd.setDate(currentEnd.getDate() - 1);
        newStart = new Date(newEnd);
        break;
      case '당월':
        newStart = new Date(today.getFullYear(), today.getMonth(), 1);
        newEnd = today;
        break;
      case '전월':
        newStart = new Date(currentStart.getFullYear(), currentStart.getMonth() - 1, 1);
        newEnd = new Date(currentStart.getFullYear(), currentStart.getMonth(), 0);
        break;
      default:
        return;
    }
    setStartDate(formatDate(newStart));
    setEndDate(formatDate(newEnd));
  };

  const fetchTransactions = useCallback(async () => {
    setLoading(true);
    const baseUrl = process.env.REACT_APP_API || '';
    // 기간을 한국시간(KST) 기준으로 해석 후 UTC ISO 문자열로 전달
    const dateFrom = dayjs
      .utc(startDate.replace(/\./g, '-') + 'T00:00:00')
      .subtract(9, 'hour')
      .toISOString();
    const dateTo = dayjs
      .utc(endDate.replace(/\./g, '-') + 'T23:59:59.999')
      .subtract(9, 'hour')
      .toISOString();
    try {
      // GET /api/cp/payment/manual - page, limit, status, dateFrom, dateTo, q(약국명 검색)
      const params = {
        page: 1,
        limit: 100,
        dateFrom,
        dateTo,
      };
      if (searchQuery.trim()) params.q = searchQuery.trim();

      const { data } = await axios.get(baseUrl + '/api/cp/payment/manual', {
        params,
        withCredentials: true,
      });

      let rows = [];
      if (data?.code === 1000) {
        if (Array.isArray(data?.data?.rows)) {
          rows = data.data.rows;
        } else if (Array.isArray(data?.data)) {
          rows = data.data;
        }
      }

      if (Array.isArray(rows) && rows.length > 0) {
        const filteredRows = rows.filter((item) => {
          const status = String(item.status || '').toUpperCase();
          return status !== 'FAILED';
        });
        setTransactions(filteredRows.map(mapPaymentToTransaction));
      } else {
        setTransactions([]);
      }
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate, searchQuery]);

  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  const totalCount = transactions.length;
  const totalAmount = transactions.reduce(
    (sum, item) => sum + Number(item.amount || 0),
    0
  );

  const handleConfirmCancel = async () => {
    if (!selectedItem) return;
    const paymentTransactionId = selectedItem.paymentTransactionId;
    if (paymentTransactionId == null) {
      showToast('취소할 결제 ID가 없습니다.', 'error');
      return;
    }

    setCancelModalOpen(false);

    const baseUrl = process.env.REACT_APP_API || '';
    try {
      const { data } = await axios.post(
        baseUrl + '/api/cp/payment/manual/cancel',
        {
          paymentTransactionId,
          reason: '가맹점 취소 요청',
        },
        { withCredentials: true }
      );

      const status = data?.data?.status;
      const providerResult = data?.data?.provider_result || {};
      const success = providerResult?.success !== false && status === 'COMPLETED';

      if (data?.code === 1000 && success) {
        showToast(data?.msg || '결제취소가 완료되었습니다.');
        // 목록 전체를 다시 불러와서 테이블/상세 정보를 동기화
        fetchTransactions();
        // 이미 열려 있던 상세정보에도 취소 상태를 반영해서 취소 버튼이 사라지도록 처리
        setSelectedItem((prev) => {
          if (!prev) return prev;
          if (prev.paymentTransactionId !== paymentTransactionId) return prev;
          return {
            ...prev,
            cancelStatus: true,
            status: 'CANCELED',
          };
        });
      } else {
        const msg =
          data?.data?.outRsltMsg ?? data?.data?.provider_result?.outRsltMsg ?? data?.msg ?? '결제취소에 실패했습니다.';
        showToast(msg, 'error');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.msg || '결제취소에 실패했습니다.';
      showToast(msg, 'error');
    }
  };

  const handleConfirmSendReceipt = async () => {
    const email = (receiptEmail || '').trim();
    if (!email) {
      showToast('전송할 이메일을 입력해주세요.', 'error');
      return;
    }
    if (!selectedItem) return;

    const paymentTransactionId = selectedItem.paymentTransactionId;
    if (paymentTransactionId == null) {
      showToast('전표 ID가 없습니다.', 'error');
      return;
    }

    const baseUrl = process.env.REACT_APP_API || '';
    try {
      setIsSendingReceipt(true);
      const { data } = await axios.post(
        baseUrl + '/api/cp/payment/manual/receipt-email',
        {
          paymentTransactionId,
          payment_transaction_id: paymentTransactionId,
          email,
        },
        { withCredentials: true }
      );

      if (data?.code === 1000) {
        showToast(data?.msg || '이메일로 전표가 전송되었습니다.');
        setReceiptModalOpen(false);
        setReceiptEmail('');
      } else {
        showToast(data?.msg || '전표 이메일 전송에 실패했습니다.', 'error');
      }
    } catch (err) {
      const msg =
        err?.response?.data?.msg ||
        '전표 이메일 전송 중 오류가 발생했습니다.';
      showToast(msg, 'error');
    } finally {
      setIsSendingReceipt(false);
    }
  };

  return (
    <>
      <ManagementPage
        title="결제내역"
        emptyStateIcon={<CreditCard size={32} className="opacity-20" />}
        hideSearch={true}
        filters={
          <div className="flex items-center gap-3 flex-wrap">
            <div className="h-[42px] flex items-center gap-2 bg-[#f2f4f6] px-3 rounded-lg border border-transparent focus-within:border-[#0064ff] transition-all shrink-0 w-[250px]">
              <input
                type="text"
                placeholder="약국명 검색"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    setSearchQuery(searchInput.trim());
                  }
                }}
                className="bg-transparent border-none outline-none text-[14px] text-[#191f28] py-0 h-full flex-1 min-w-0"
              />
              <button
                type="button"
                onClick={() => setSearchQuery(searchInput.trim())}
                className="px-2 py-1 rounded bg-[#0064ff] text-white text-[12px] font-bold hover:opacity-90 shrink-0"
              >
                검색
              </button>
            </div>
            <div className="h-4 w-[1px] bg-[#e5e8eb]" />
            <div className="h-[42px] flex items-center gap-1.5 bg-[#f2f4f6] px-3 rounded-lg border border-transparent focus-within:border-[#0064ff] transition-all">
              <input
                type="date"
                value={startDate.replace(/\./g, '-')}
                onChange={(e) =>
                  setStartDate(e.target.value.replace(/-/g, '.'))
                }
                className="bg-transparent border-none outline-none text-[14px] font-mono text-[#191f28] py-0 h-full"
              />
              <span className="text-[#8b95a1] text-[12px]">~</span>
              <input
                type="date"
                value={endDate.replace(/\./g, '-')}
                onChange={(e) =>
                  setEndDate(e.target.value.replace(/-/g, '.'))
                }
                className="bg-transparent border-none outline-none text-[14px] font-mono text-[#191f28] py-0 h-full"
              />
            </div>
            <div className="flex gap-1">
              {['당일', '전일', '당월', '전월'].map((type) => (
                <button
                  key={type}
                  onClick={() => setQuickPeriod(type)}
                  className="px-2.5 py-1.5 bg-white border border-[#e5e8eb] rounded-lg text-[12px] font-bold text-[#4e5968] hover:bg-[#f2f4f6] leading-none h-[42px] flex items-center"
                >
                  {type}
                </button>
              ))}
            </div>
          </div>
        }
        leftActions={
          <div className="text-[16px] font-bold text-[#191f28]">
            총 결제 금액:{' '}
            <span className="text-[#0064ff]">
              {totalAmount.toLocaleString()}원
            </span>{' '}
            ({totalCount}건)
          </div>
        }
        onExcelDownload={() => {
          if (!transactions || transactions.length === 0) {
            showToast('다운로드할 결제내역이 없습니다.', 'error');
            return;
          }

          // 관리자 페이지와 동일한 형식의 CSV 생성
          const csv = buildCsv(transactions);
          const blob = new Blob([csv], {
            type: 'text/csv;charset=utf-8;',
          });
          const url = URL.createObjectURL(blob);

          const safeStart = startDate.replace(/\./g, '');
          const safeEnd = endDate.replace(/\./g, '');
          const filename = `결제내역_${safeStart}_${safeEnd}.csv`;

          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          URL.revokeObjectURL(url);

          showToast('엑셀 다운로드가 시작되었습니다.');
        }}
        excelButtonClassName="inline-flex items-center gap-2 px-3 py-1.5 bg-[#191f28] text-white rounded-lg font-bold text-[14px] hover:bg-black transition-colors"
        tableHeaders={
          <>
            <th className="px-4 py-3">결제일</th>
            <th className="px-4 py-3">약국명</th>
            <th className="px-4 py-3">상태</th>
            <th className="px-4 py-3 text-right">금액</th>
            <th className="px-4 py-3 text-right">수수료</th>
            <th className="px-4 py-3">영업자</th>
          </>
        }
        data={transactions}
        renderRow={(item) => {
          const isCanceled = item.cancelStatus;
          const rowBg = isCanceled ? ' bg-[#fef2f2]' : '';
          return (
            <>
              <td className={`px-4 py-3 text-[#4e5968] whitespace-nowrap${rowBg}`}>
                {formatKST(item.date, 'YYYY.MM.DD HH:mm')}
              </td>
              <td className={`px-4 py-3 font-bold text-[#191f28]${rowBg}`}>
                {item.pharmacy}
              </td>
              <td className={`px-4 py-3 text-[#4e5968]${rowBg}`}>
                {getStatusLabelFromTx(item)}
              </td>
              <td className={`px-4 py-3 text-right text-[#191f28]${rowBg}`}>
                {Number(item.amount || 0).toLocaleString()}원
              </td>
              <td className={`px-4 py-3 text-right text-[#4e5968]${rowBg}`}>
                {Number(item.fee ?? 0).toLocaleString()}원
              </td>
              <td className={`px-4 py-3 text-[#4e5968]${rowBg}`}>{item.driver || '-'}</td>
            </>
          );
        }}
        detailTitle="결제 상세정보"
        selectedItem={selectedItem}
        onSelectItem={setSelectedItem}
        detailButtonLabel={selectedItem ? 'email 전표전송' : undefined}
        detailButtonIcon={selectedItem ? <CreditCard size={14} /> : undefined}
        detailButtonSmall
        detailButtonDisabled={!selectedItem}
        onDetailButtonClick={() => {
          if (!selectedItem) return;
          setReceiptModalOpen(true);
        }}
        renderDetail={(item) => (
          <>
            {/* 상세 상단 오른쪽: 취소 버튼만 (승인 건에서만 표시) */}
            <div className="mb-3 flex justify-end">
              {!item.cancelStatus && (
                <button
                  type="button"
                  onClick={() => setCancelModalOpen(true)}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-[#fecaca] text-[13px] font-semibold text-[#b91c1c] bg-[#fef2f2] hover:bg-[#fee2e2]"
                >
                  <XCircle size={14} />
                  <span>취소</span>
                </button>
              )}
            </div>
            <DetailGrid>
              <DetailItem
                label="결제일"
                value={formatKST(item.date, 'YYYY.MM.DD HH:mm')}
              />
              <DetailItem label="약국명" value={item.pharmacy} />
              <DetailItem
                label="금액"
                value={`${Number(item.amount || 0).toLocaleString()}원`}
                color="#191f28"
              />
              <DetailItem
                label="수수료"
                value={`${Number(item.fee ?? 0).toLocaleString()}원`}
              />
              <DetailItem label="상태" value={getStatusLabelFromTx(item)} />
              <DetailItem label="할부" value={item.type} />
              <DetailItem label="영업자" value={item.driver || '-'} />
              <DetailItem label="승인번호" value={item.approvalNo || '-'} />
            </DetailGrid>
          </>
        )}
      />

      {/* 취소 확인 모달 */}
      {cancelModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-2xl px-6 py-5 w-full max-w-[360px] shadow-xl">
            <div className="text-[18px] font-bold text-[#111827] mb-2">
              결제를 취소하시겠습니까?
            </div>
            <div className="text-[14px] text-[#6b7280] mb-5 leading-relaxed">
              선택한 거래(
              <span className="font-semibold">{selectedItem.pharmacy}</span>,{' '}
              <span className="font-semibold">
                {Number(selectedItem.amount || 0).toLocaleString()}원
              </span>
              )를 취소 처리합니다.
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => setCancelModalOpen(false)}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#e5e7eb] text-[#111827]"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleConfirmCancel}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#ef4444] text-white"
              >
                취소하기
              </button>
            </div>
          </div>
        </div>
      )}

      {/* email 전표전송 모달 */}
      {receiptModalOpen && selectedItem && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[1000]">
          <div className="bg-white rounded-2xl px-6 py-5 w-full max-w-[360px] shadow-xl">
            <div className="text-[18px] font-bold text-[#111827] mb-2">
              email 전표전송
            </div>
            <div className="text-[14px] text-[#6b7280] mb-4 leading-relaxed">
              전표를 받을 이메일 주소를 입력해 주세요.
            </div>
            <input
              type="email"
              value={receiptEmail}
              onChange={(e) => setReceiptEmail(e.target.value)}
              placeholder="example@email.com"
              className="w-full mb-4 px-3 py-2.5 rounded-xl border border-[#d1d5db] text-[14px] outline-none focus:border-[#2563eb]"
            />
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => {
                  if (isSendingReceipt) return;
                  setReceiptModalOpen(false);
                  setReceiptEmail('');
                }}
                className="flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold bg-[#e5e7eb] text-[#111827]"
              >
                닫기
              </button>
              <button
                type="button"
                onClick={handleConfirmSendReceipt}
                disabled={isSendingReceipt}
                className={`flex-1 px-3 py-2.5 rounded-xl text-[14px] font-semibold text-white ${
                  isSendingReceipt ? 'bg-[#93c5fd] cursor-not-allowed' : 'bg-[#2563eb]'
                }`}
              >
                {isSendingReceipt ? '전송중...' : '전송'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PaymentList;

